import { orderBy, uniq } from 'lodash';
// A "Ducks"-style collection
// https://github.com/erikras/ducks-modular-redux
import { exportActionTypes } from 'constants/exportSelector';
import { exportTypes } from 'constants/values';

import {
  toggleDriveModal,
  toggleErrorDriveModal,
  selectMeasurementType,
  selectMeasurementDistance,
  selectPreferredDevice,
  setExportOptionsIsOpen,
  showCreateJDWorkPlanModal
} from 'current/uiwidgets';

// Selectors
import { hasAffiliateById } from 'affiliates/selectors';
import { getDeviceByName, getDeviceById, getPreferredDevice, getDriveDevices } from 'devices/selectors';
import {
  onlyExportPrimaryScripts
} from 'export-selector/selectors';

// Actions
import { bulkSendToAffiliate, connectToAffiliate } from 'affiliates';
import { savePreferredDevice, saveSelectedDevice, saveFormat } from 'devices';
import { bulkDownloadScripts } from 'scripts';
import { scriptTypes } from '../constants/scripts';

// Action Types
export const typePrefix = 'vra/export-selector/';
export const actions = [
  'ADD_RAW_SCRIPTS',
  'SET_EXPORT_OPTIONS',
  'SET_EXPORT_PARAMS',
  'STORE_RAW_SCRIPT_LIST',
  'SET_FV_FIELDS_FOR_WORK_PLANS',
  'TOGGLE_FV_FIELD_FOR_WORK_PLANS',
  'REMOVE_FV_FIELD_FOR_WORK_PLANS'
].reduce((result, key) => { result[key] = `${typePrefix}${key}`; return result; }, {});

// Reducer

export default function reducer(
  state = { rawScriptList: [], isFvFieldSelected: {}, params: {} },
  action = {}
) {
  switch (action.type) {
    case actions.SET_EXPORT_OPTIONS:
      return {
        ...state,
        ...action.payload
      };
    case actions.SET_EXPORT_PARAMS:
      return {
        ...state,
        ...action.payload
      };
    case actions.STORE_RAW_SCRIPT_LIST:
      return {
        ...state,
        ...action.payload
      };
    case actions.SET_FV_FIELDS_FOR_WORK_PLANS:
      return {
        ...state,
        isFvFieldSelected: {
          ...state.isFvFieldSelected,
          ...action.payload
        }
      };
    case actions.TOGGLE_FV_FIELD_FOR_WORK_PLANS: {
      const isFvFieldSelected = { ...state.isFvFieldSelected };
      isFvFieldSelected[action.payload] = isFvFieldSelected[action.payload] ? false : true;

      return {
        ...state,
        isFvFieldSelected,
      };
    }
    case actions.REMOVE_FV_FIELD_FOR_WORK_PLANS: {
      return {
        ...state,
        isFvFieldSelected: {
          ...state.isFvFieldSelected,
          [action.payload]: false
        }
      };
    }
    case actions.ADD_RAW_SCRIPTS: {
      return {
        ...state,
        rawScriptList: [...state.rawScriptList, ...action.payload]
      };
    }
    default:
      return state;
  }
}

// Action Creators

export const setExportParams = (params) => {
  return {
    type: actions.SET_EXPORT_PARAMS,
    payload: { params }
  };
};

export const setExportOptions = (options) => {
  return {
    type: actions.SET_EXPORT_OPTIONS,
    payload: { options }
  };
};

const exportToAffiliate = ({dispatch, state, affiliateId}) => {
  if (hasAffiliateById(state, affiliateId)) {
    dispatch(bulkSendToAffiliate({ affiliateId }));
  } else {
    dispatch(connectToAffiliate({ affiliateId }));
  }
};

const setPreferredDevice = ({dispatch, getState, newPreferredDevice }) => {
  const state = getState();
  const { label: currentPreferredDevice } = getPreferredDevice(state);
  if (newPreferredDevice !== currentPreferredDevice) {
    const device = getDeviceByName(state, newPreferredDevice);
    dispatch(savePreferredDevice(device));
  }
};

const sendToDrive = ({dispatch, getState, formatSelected}) => {
  const state = getState();
  const driveDevices = getDriveDevices(state);
  const fullDevice = driveDevices.find(({name}) => name === formatSelected);
  try {
    dispatch(toggleDriveModal(true));
    dispatch(saveFormat({
      deviceLabel: formatSelected
    }));
    dispatch(saveSelectedDevice({
      name: fullDevice.name,
      description: fullDevice.description,
      id: fullDevice.id,
      deviceModelId: fullDevice.deviceModelId
    }));
  } catch (error) {
    dispatch(toggleErrorDriveModal(true));
  }
};

const openModalForStara=(state, dispatch) => {
  dispatch(setExportOptionsIsOpen({ exportType: exportTypes.BULK, status: true }));
  const staraDevice = getDeviceById(state, 'stara-shapefile');
  dispatch(selectPreferredDevice(staraDevice));
  dispatch(selectMeasurementType());
  dispatch(selectMeasurementDistance());
};

export const selectExportOption = (paramString) => {
  return (dispatch, getState) => {
    const state = getState();
    dispatch(selectMeasurementType());
    dispatch(selectMeasurementDistance());

    const [ action, actionParam, scopeTypeAndOperationUuid, scriptType, primaryOnly, scripts] = paramString.split('|');
    const [ scopeType, operationUuid ] = scopeTypeAndOperationUuid.split('~');

    dispatch(setExportParams({
      scopeType,
      operationUuid,
      scriptType,
      primaryOnly: onlyExportPrimaryScripts(state, primaryOnly),
      scripts
    }));

    if(paramString.includes('Stara') && scriptType === scriptTypes.PLANTING) {
      openModalForStara(state, dispatch);
      return;
    }

    switch(action) {
      case exportActionTypes.EXPORT_TO_AFFILIATE:
        exportToAffiliate({ dispatch, state, affiliateId: actionParam });
        return;
      case exportActionTypes.DOWNLOAD_PREFERRED_DEVICE_FORMAT:
        setPreferredDevice({ dispatch, getState, newPreferredDevice: actionParam });
        dispatch(bulkDownloadScripts());
        return;
      case exportActionTypes.SEND_TO_DRIVE:
        sendToDrive({ dispatch, getState, formatSelected: actionParam });
        return;
      case exportActionTypes.CREATE_WORK_PLAN:
        dispatch(showCreateJDWorkPlanModal());
        return;
      default:
    }
  };
};

export const storeRawScriptList = (rawScriptList) => {
  // uniq it in case updated scripts are included -- order by update time descending
  // so uniq takes the updated scripts
  rawScriptList = uniq(orderBy(rawScriptList, ['update-time'],['desc']));
  return {
    type: actions.STORE_RAW_SCRIPT_LIST,
    payload: { rawScriptList }
  };
};

export const toggleFVFieldForWorkPlans = (fieldUuid) => {
  return {
    type: actions.TOGGLE_FV_FIELD_FOR_WORK_PLANS,
    payload: fieldUuid
  };
};

export const removeFVFieldForWorkPlans = (fieldUuid) => {
  return {
    type: actions.REMOVE_FV_FIELD_FOR_WORK_PLANS,
    payload: fieldUuid
  };
};

export const setFVFieldsForWorkPlans = (selectedFvFields) => {
  return {
    type: actions.SET_FV_FIELDS_FOR_WORK_PLANS,
    payload: selectedFvFields
  };
};

export const addRawScripts = (rawList) => {
  return {
    type: actions.ADD_RAW_SCRIPTS,
    payload: rawList
  };
};
